// src/pages/admin/AddEditCompanyModal.js
import React, { useState, useEffect } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Box
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Axios from "axios";

export default function AddEditCompanyModal({ open, onClose, company, onSaveSuccess }) {
  const token = localStorage.getItem("token");
  const isEdit = !!company;

  const [formData, setFormData] = useState({
    name: "",
    code: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || "",
        code: company.code || "",
      });
    } else {
      setFormData({
        name: "",
        code: "",
      });
    }
    setError("");
  }, [company, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name || !formData.code) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      if (isEdit) {
        // Update existing company
        await Axios.patch(
          `${process.env.REACT_APP_BASE_URL}admin/company/${company._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new company
        await Axios.post(
          `${process.env.REACT_APP_BASE_URL}admin/company`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      onSaveSuccess();
    } catch (e) {
      setError(
        e.response?.data?.message ||
        `Failed to ${isEdit ? "update" : "create"} company`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit Company" : "Add New Company"}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Company Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter company name"
            required
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Company Code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            placeholder="Enter company code (e.g., COMP1)"
            required
            helperText="A unique code to identify this company. Letters and numbers only."
            inputProps={{ style: { textTransform: "uppercase" } }}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" type="submit" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update Company" : "Create Company"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
